<template>
  <div class="flow-row">
    <div class="flow-nodes"
      @dragover.prevent="onRowDragOver"
      @dragleave="dragOver = false; dragRejected = false"
      @drop.prevent="onDrop"
    >
      <!-- NODE ATRIBUIÇÃO (valor + nome do usuário) -->
      <div
        class="flow-node flow-node-result"
        draggable="true"
        @dragstart.stop="onDragResult($event)"
      >
        <div class="flow-node-box result-box" :title="'Arraste para usar ' + linha.resultado + ' em outra etapa'">
          <span v-if="temErro" class="result-val result-erro">Erro</span>
          <span v-else-if="valorPreview !== null" class="result-val" :class="{ neg: valorPreview < 0 }">
            {{ formatNum(valorPreview) }}
          </span>
          <span v-else class="result-val result-empty">?</span>
          <button
            v-if="tokens.length === 0"
            class="flow-token-del"
            @click="$emit('remover', index)"
            title="Remover linha"
          >
            <fa-icon icon="times" />
          </button>
        </div>
        <input
          type="text"
          :value="linha.resultado"
          @input="onResultadoInput($event)"
          @blur="garantirNome"
          @keydown.enter="$event.target.blur()"
          @keydown.space.prevent
          placeholder="Clique para nomear"
          spellcheck="false"
          class="flow-node-label flow-node-label-edit"
          ref="resultadoInput"
          :title="'Editar nome: ' + (linha.resultado || 'sem nome')"
        />
      </div>

      <!-- WIRE + EQUAL NODE -->
      <div class="flow-wire"></div>
      <div class="flow-node">
        <div class="flow-node-box eq-box">
          <span class="eq-sign">=</span>
        </div>
      </div>

      <div class="flow-wire" v-if="tokens.length === 0"></div>

      <!-- Wire zone antes do primeiro token (expande igual às do meio) -->
      <div
        v-if="tokens.length > 0"
        class="flow-wire-zone"
        :class="{ 'flow-wire-zone-expand': dragOverWire === -1 && !dragRejected, 'flow-wire-zone-rejected': dragOverWire === -1 && dragRejected }"
        @dragenter.prevent="onWireEnter(-1)"
        @dragover.prevent
        @dragleave="onWireLeave(-1)"
        @drop.prevent="onDropWireBefore($event)"
      >
        <div class="flow-wire-line"></div>
        <div class="flow-wire-dot flow-wire-dot-l"></div>
        <div class="flow-wire-dot flow-wire-dot-r"></div>
      </div>

      <!-- TOKENS -->
      <template v-for="(tk, ti) in tokens">
        <!-- SLOT de inserção antes do token (se ativo) -->
        <div
          :key="'s' + ti"
          v-if="activeSlot === ti"
          class="flow-node flow-node-slot"
        >
          <div class="flow-node-box box-slot">
            <fa-icon icon="plus" />
          </div>
          <span class="flow-node-label slot-label">Selecione</span>
        </div>
        <div :key="'sw' + ti" v-if="activeSlot === ti" class="flow-wire"></div>

        <div
          :key="'n' + ti"
          class="flow-node"
          :class="['flow-node-' + tk.tipo, removingIndex === ti ? 'flow-node-removing' : '', justInsertedIndex === ti ? 'flow-node-just-added' : '']"
        >
          <div class="flow-node-box" :class="boxClass(tk)" :title="tk.fullName">
            <span v-if="tk.tipo === 'var'" class="flow-node-icon-text">{{ tk.initials }}</span>
            <span v-else-if="tk.tipo === 'op'" class="flow-node-icon-op">{{ tk.display }}</span>
            <input
              v-else-if="tk.tipo === 'num'"
              type="number"
              :value="tk.display"
              @change="editarNumero(ti, $event.target.value)"
              step="any"
              class="flow-num-input"
              title="Editar valor fixo"
            />
            <span v-else class="flow-node-icon-text">{{ tk.display }}</span>
            <button class="flow-token-del" @click="removerToken(ti)" title="Remover">
              <fa-icon icon="times" />
            </button>
          </div>
          <span class="flow-node-label" :title="tk.fullName">{{ tk.label }}</span>
        </div>

        <!-- WIRE entre tokens -->
        <div
          :key="'w' + ti"
          class="flow-wire-zone"
          :class="{ 'flow-wire-zone-expand': dragOverWire === ti && !dragRejected, 'flow-wire-zone-rejected': dragOverWire === ti && dragRejected }"
          v-if="ti < tokens.length - 1"
          @dragenter.prevent="onWireEnter(ti)"
          @dragover.prevent
          @dragleave="onWireLeave(ti)"
          @drop.prevent="onDropWire($event, ti)"
        >
          <div class="flow-wire-line"></div>
          <div class="flow-wire-dot flow-wire-dot-l"></div>
          <div class="flow-wire-dot flow-wire-dot-r"></div>
          <div class="flow-wire-actions">
            <button class="flow-wire-btn flow-wire-btn-del" @click.stop="desconectarWire(ti)" title="Desconectar">
              <fa-icon icon="times" />
            </button>
          </div>
        </div>
      </template>

      <!-- SLOT no final (se ativo) -->
      <div class="flow-wire" v-if="activeSlot === 'end'"></div>
      <div v-if="activeSlot === 'end'" class="flow-node flow-node-slot">
        <div class="flow-node-box box-slot">
          <fa-icon icon="plus" />
        </div>
        <span class="flow-node-label slot-label">Selecione</span>
      </div>

      <!-- ADD NODE (+) no final -->
      <div class="flow-wire" v-if="tokens.length > 0 && activeSlot === null"></div>
      <div
        v-if="activeSlot === null"
        class="flow-node flow-node-add"
        :class="{ 'flow-node-add-active': dragOver && !dragRejected, 'flow-node-add-rejected': dragOver && dragRejected }"
        @click="iniciarInsercao('end')"
        @dragover.prevent="dragOver = true"
        @dragleave="dragOver = false"
        @drop.prevent="onDrop"
      >
        <div class="flow-node-box box-add">
          <fa-icon icon="plus" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "FormulaLinhaBuilder",
  props: {
    linha: { type: Object, required: true },
    index: { type: Number, required: true },
    temErro: { type: Boolean, default: false },
    isFocused: { type: Boolean, default: false },
    valorPreview: { type: Number, default: null },
    variaveisMap: { type: Object, default: () => ({}) },
    variaveisTipoMap: { type: Object, default: () => ({}) },
    resultadosAnteriores: { type: Array, default: () => [] },
    chavesVariaveis: { type: Array, default: () => [] },
    isLastLine: { type: Boolean, default: false },
    forceDeactivate: { type: Boolean, default: false },
  },
  data() {
    return { dragOver: false, activeSlot: null, removingIndex: null, dragOverWire: null, wireEnterCount: 0, justInsertedIndex: null, dragRejected: false };
  },
  watch: {
    forceDeactivate(val) {
      if (val && this.activeSlot !== null) this.cancelarSlot();
    },
  },
  computed: {
    tokens() {
      const expr = this.linha.expressao || "";
      if (!expr.trim()) return [];
      const tks = [];
      let pos = 0;
      const len = expr.length;
      const ops = { "+": "+", "-": "−", "*": "×", "/": "÷" };
      const labels = { "+": "Soma", "-": "Subtração", "*": "Multiplicação", "/": "Divisão" };
      while (pos < len) {
        const ch = expr[pos];
        if (/\s/.test(ch)) { pos++; continue; }
        if ("()".includes(ch)) {
          tks.push({ tipo: "paren", display: ch, initials: ch, label: ch === "(" ? "(" : ")", fullName: ch === "(" ? "Abre parêntese" : "Fecha parêntese", raw: ch });
          pos++; continue;
        }
        if ("+-*/".includes(ch)) {
          tks.push({ tipo: "op", display: ops[ch] || ch, initials: ops[ch], label: labels[ch] || ch, fullName: labels[ch] || ch, raw: ch });
          pos++; continue;
        }
        if (/[\d.]/.test(ch)) {
          let n = "";
          while (pos < len && /[\d.]/.test(expr[pos])) { n += expr[pos]; pos++; }
          tks.push({ tipo: "num", display: n, initials: n, label: "Valor " + n, fullName: "Valor fixo: " + n, raw: n });
          continue;
        }
        if (/[a-zA-Z_]/.test(ch)) {
          let v = "";
          while (pos < len && /[a-zA-Z0-9_]/.test(expr[pos])) { v += expr[pos]; pos++; }
          const nome = this.variaveisMap[v] || v;
          const initials = this.getInitials(nome);
          const varTipo = this.variaveisTipoMap[v] || null;
          tks.push({ tipo: "var", display: nome, initials, label: nome, fullName: nome, chave: v, raw: v, varTipo });
          continue;
        }
        pos++;
      }
      return tks;
    },
  },
  methods: {
    getInitials(nome) {
      const words = nome.split(/[\s_]+/).filter(Boolean);
      const stopWords = ['de', 'do', 'da', 'dos', 'das', 'os', 'as', 'o', 'a', 'com', 'sem', 'em', 'no', 'na', 'e', 'ou'];
      const meaningful = words.filter((w) => !stopWords.includes(w.toLowerCase()));
      if (meaningful.length >= 3) {
        return (meaningful[0][0] + meaningful[meaningful.length - 1][0]).toUpperCase();
      }
      if (meaningful.length === 2) return (meaningful[0][0] + meaningful[1][0]).toUpperCase();
      if (words.length >= 2) return (words[0][0] + words[1][0]).toUpperCase();
      return nome.substring(0, 2).toUpperCase();
    },
    onDragResult(e) {
      if (!this.linha.resultado) return;
      e.dataTransfer.setData("text/plain", this.linha.resultado);
      e.dataTransfer.effectAllowed = "copy";
      window.__formulaDragData = this.linha.resultado;
      const box = e.target.closest('.flow-node-result')?.querySelector('.result-box');
      if (box) e.dataTransfer.setDragImage(box, 30, 30);
      const onEnd = () => { window.__formulaDragData = null; document.removeEventListener('dragend', onEnd); };
      document.addEventListener('dragend', onEnd);
    },
    boxClass(tk) {
      if (tk.tipo !== 'var') return 'box-' + tk.tipo;
      if (tk.varTipo) return 'box-var-' + tk.varTipo;
      return 'box-var';
    },
    inserirNoSlot(texto) {
      if (!this.isDropAllowed(texto)) return;
      const atual = this.linha.expressao || "";
      let insertIdx;
      if (this.activeSlot === null || this.activeSlot === "end") {
        const sep = atual && !atual.endsWith(" ") && !atual.endsWith("(") ? " " : "";
        this.$emit("atualizar", this.index, "expressao", atual + sep + texto);
        insertIdx = this.tokens.length;
      } else {
        insertIdx = this.activeSlot;
        const parts = this.tokens.map((t) => t.raw);
        parts.splice(this.activeSlot, 0, texto);
        this.$emit("atualizar", this.index, "expressao", parts.join(" "));
      }
      this.activeSlot = null;
      this.$emit("pending", false);
      this.$nextTick(() => { this.flashInserted(insertIdx); });
    },
    isDropAllowed(chave) {
      if (!chave) return false;
      const val = chave.trim();
      if (this.chavesVariaveis.includes(val)) return true;
      if (this.resultadosAnteriores.includes(val)) return true;
      if (/^[\s()+\-*/.\d]+$/.test(val)) return true;
      return false;
    },
    checkDragRejected() {
      const dragging = window.__formulaDragData;
      if (!dragging) { this.dragRejected = false; return; }
      this.dragRejected = !this.isDropAllowed(dragging);
    },
    onRowDragOver(e) {
      this.dragOver = true;
      this.checkDragRejected();
      e.dataTransfer.dropEffect = this.dragRejected ? "none" : "copy";
    },
    onWireEnter(ti) {
      this.wireEnterCount++;
      this.dragOverWire = ti;
      this.checkDragRejected();
    },
    onWireLeave(ti) {
      this.wireEnterCount--;
      if (this.wireEnterCount <= 0) {
        this.wireEnterCount = 0;
        if (this.dragOverWire === ti) this.dragOverWire = null;
        this.dragRejected = false;
      }
    },
    onDropWireBefore(e) {
      this.dragOverWire = null;
      this.wireEnterCount = 0;
      this.dragOver = false;
      const chave = e.dataTransfer.getData("text/plain");
      if (!chave) return;
      e.stopPropagation();
      if (!this.isDropAllowed(chave)) return;
      const parts = this.tokens.map((t) => t.raw);
      parts.splice(0, 0, chave.trim());
      this.$emit("atualizar", this.index, "expressao", parts.join(" "));
      this.flashInserted(0);
    },
    onDropWire(e, afterTokenIndex) {
      this.dragOverWire = null;
      this.wireEnterCount = 0;
      this.dragOver = false;
      const chave = e.dataTransfer.getData("text/plain");
      if (!chave) return;
      e.stopPropagation();
      if (!this.isDropAllowed(chave)) return;
      const insertAt = afterTokenIndex + 1;
      const parts = this.tokens.map((t) => t.raw);
      parts.splice(insertAt, 0, chave.trim());
      this.$emit("atualizar", this.index, "expressao", parts.join(" "));
      this.flashInserted(insertAt);
    },
    onDrop(e) {
      this.dragOver = false;
      this.dragOverWire = null;
      this.wireEnterCount = 0;
      e.stopPropagation();
      const chave = e.dataTransfer.getData("text/plain");
      if (!chave) return;
      if (!this.isDropAllowed(chave)) return;
      const atual = this.linha.expressao || "";
      const val = chave.trim();
      const sep = atual && !atual.endsWith(" ") && !atual.endsWith("(") ? " " : "";
      this.$emit("atualizar", this.index, "expressao", atual + sep + val);
      this.$nextTick(() => {
        const idx = this.tokens.length - 1;
        this.flashInserted(idx);
        if (val === '0') this.focusNumInput(idx);
      });
    },
    focusNumInput(tokenIndex) {
      this.$nextTick(() => {
        const inputs = this.$el.querySelectorAll('.flow-num-input');
        let count = 0;
        for (let i = 0; i < this.tokens.length; i++) {
          if (this.tokens[i].tipo === 'num') {
            if (i === tokenIndex) {
              const input = inputs[count];
              if (input) { input.focus(); input.select(); }
              return;
            }
            count++;
          }
        }
      });
    },
    flashInserted(idx) {
      this.justInsertedIndex = idx;
      setTimeout(() => { this.justInsertedIndex = null; }, 800);
    },
    removerToken(tokenIndex) {
      this.removingIndex = tokenIndex;
      setTimeout(() => {
        const newExpr = this.tokens.filter((_, i) => i !== tokenIndex).map((t) => t.raw).join(" ");
        this.$emit("atualizar", this.index, "expressao", newExpr);
        this.removingIndex = null;
      }, 200);
    },
    desconectarWire(afterTokenIndex) {
      this.activeSlot = afterTokenIndex + 1;
      this.$emit("pending", true);
    },
    iniciarInsercao(pos) {
      if (this.activeSlot === pos) {
        this.cancelarSlot();
        return;
      }
      this.activeSlot = pos;
      this.$emit("pending", true);
    },
    cancelarSlot() {
      this.activeSlot = null;
      this.$emit("pending", false);
    },
    editarNumero(tokenIndex, novoValor) {
      if (!novoValor && novoValor !== '0') novoValor = '0';
      const parts = this.tokens.map((t) => t.raw);
      parts[tokenIndex] = novoValor;
      this.$emit("atualizar", this.index, "expressao", parts.join(" "));
    },
    onResultadoInput(e) {
      const val = e.target.value.replace(/[^a-zA-Z0-9_]/g, '_').replace(/^(\d)/, '_$1');
      e.target.value = val;
      this.$emit('atualizar', this.index, 'resultado', val);
    },
    garantirNome() {
      if (!this.linha.resultado || !this.linha.resultado.trim()) {
        this.$emit("atualizar", this.index, "resultado", "etapa_" + (this.index + 1));
      }
    },
    blurResultado() {
      if (this.$refs.resultadoInput) this.$refs.resultadoInput.blur();
    },
    formatNum(v) {
      if (v === null || v === undefined) return "";
      return Number(v).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
  },
};
</script>

<style scoped>
.flow-row {
  display: flex; flex-direction: column; align-items: center;
  padding: 8px 0; position: relative;
}
.flow-nodes {
  display: flex; align-items: flex-start; justify-content: center; gap: 0;
  position: relative; min-height: 80px;
}

/* NODE */
.flow-node {
  display: flex; flex-direction: column; align-items: center;
  gap: 4px; flex-shrink: 0;
}
.flow-node-box {
  width: 60px; height: 60px; border-radius: 12px;
  display: flex; align-items: center; justify-content: center;
  border: 1.5px solid #e5e7eb; background: #fff;
  transition: all 0.2s; position: relative;
  box-shadow: 0 1px 3px rgba(0,0,0,0.04);
}
.flow-node-box:hover {
  box-shadow: 0 4px 16px rgba(0,0,0,0.08);
  transform: translateY(-1px);
}
.flow-node-label {
  font-size: 0.62rem; color: #6b7280;
  max-width: 80px; text-align: center;
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}
.flow-node-label-edit {
  border: 1px solid transparent; background: transparent;
  font-size: 0.62rem; font-weight: 500; color: #374151;
  text-align: center; outline: none; width: 90px; padding: 1px 2px;
  border-radius: 4px; transition: all 0.15s;
}
.flow-node-label-edit:hover { border-color: #c7d2fe; background: #f5f3ff; }
.flow-node-label-edit:focus { border-color: #818cf8; background: #fff; }
.flow-node-label-edit::placeholder { color: #c7d2fe; font-style: italic; }

/* RESULTADO (valor no meio, nome embaixo) */
.flow-node-result { cursor: grab; }
.flow-node-result:active { cursor: grabbing; }
.result-box {
  border-color: #c7d2fe; background: linear-gradient(135deg, #eef2ff, #e0e7ff);
}
.result-val {
  font-family: 'SF Mono', monospace; font-size: 0.82rem;
  font-weight: 800; color: #4f46e5;
}
.result-val.neg { color: #ea580c; }
.result-erro { color: #dc2626; font-size: 0.65rem; font-weight: 700; }
.result-empty { color: #c7d2fe; font-size: 1.2rem; }

/* EQUAL */
.eq-box {
  width: 36px; height: 36px; border-radius: 8px;
  border-color: #d1d5db; background: #f9fafb;
  margin-top: 12px;
}
.eq-sign { font-size: 1.1rem; font-weight: 700; color: #9ca3af; }

/* REMOVE ANIMATION */
.flow-node-removing .flow-node-box {
  transform: scale(0); opacity: 0;
  transition: all 0.2s ease;
}
.flow-node-removing .flow-node-label { opacity: 0; transition: opacity 0.2s; }

/* JUST ADDED ANIMATION */
.flow-node-just-added .flow-node-box {
  animation: nodePopIn 0.5s cubic-bezier(0.34, 1.56, 0.64, 1);
}
@keyframes nodePopIn {
  0% { transform: scale(0.5); opacity: 0.3; box-shadow: 0 0 0 0 rgba(99,102,241,0.4); }
  50% { transform: scale(1.08); box-shadow: 0 0 0 10px rgba(99,102,241,0); }
  100% { transform: scale(1); opacity: 1; box-shadow: 0 1px 3px rgba(0,0,0,0.04); }
}

/* TOKEN DELETE */
.flow-token-del {
  position: absolute; top: -7px; right: -7px;
  width: 18px; height: 18px; border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
  border: 1.5px solid #fca5a5; background: #fff;
  color: #ef4444; cursor: pointer; font-size: 0.5rem;
  transition: all 0.15s; opacity: 0; z-index: 2;
  box-shadow: 0 1px 4px rgba(239,68,68,0.2);
}
.flow-node:hover .flow-token-del { opacity: 1; }
.flow-token-del:hover { color: #fff; background: #ef4444; border-color: #ef4444; transform: scale(1.15); }

/* VARIÁVEL — cores por tipo do backend */
.box-var { border-color: #a7f3d0; background: linear-gradient(135deg, #ecfdf5, #d1fae5); }
.box-var .flow-node-icon-text { color: #065f46; font-size: 0.85rem; font-weight: 800; }
.box-var-valor { border-color: #a7f3d0; background: linear-gradient(135deg, #ecfdf5, #d1fae5); }
.box-var-valor .flow-node-icon-text { color: #065f46; font-size: 0.85rem; font-weight: 800; }
.box-var-percentual { border-color: #fdba74; background: linear-gradient(135deg, #fff7ed, #ffedd5); }
.box-var-percentual .flow-node-icon-text { color: #9a3412; font-size: 0.85rem; font-weight: 800; }
.box-var-quantidade { border-color: #93c5fd; background: linear-gradient(135deg, #eff6ff, #dbeafe); }
.box-var-quantidade .flow-node-icon-text { color: #1e40af; font-size: 0.85rem; font-weight: 800; }
.box-var-fator { border-color: #c4b5fd; background: linear-gradient(135deg, #f5f3ff, #ede9fe); }
.box-var-fator .flow-node-icon-text { color: #5b21b6; font-size: 0.85rem; font-weight: 800; }

/* OPERADOR */
.box-op { border-color: #ddd6fe; background: linear-gradient(135deg, #f5f3ff, #ede9fe); }
.flow-node-icon-op { font-size: 1.4rem; font-weight: 600; color: #7c3aed; }

/* NÚMERO */
.box-num { border-color: #fde68a; background: linear-gradient(135deg, #fefce8, #fef3c7); }
.box-num .flow-node-icon-text { color: #92400e; font-family: 'SF Mono', monospace; font-size: 0.85rem; font-weight: 700; }
.flow-num-input {
  width: 44px; border: none; background: transparent;
  font-family: 'SF Mono', monospace; font-size: 0.85rem;
  font-weight: 700; color: #92400e; text-align: center;
  outline: none; -moz-appearance: textfield;
}
.flow-num-input::-webkit-inner-spin-button, .flow-num-input::-webkit-outer-spin-button { -webkit-appearance: none; }
.flow-num-input:focus { background: rgba(255,255,255,0.5); border-radius: 4px; }

/* PARÊNTESE */
.box-paren { border-color: #d1d5db; background: #f9fafb; width: 36px; }
.box-paren .flow-node-icon-text { color: #9ca3af; font-size: 1.3rem; font-weight: 300; }

/* SLOT DE INSERÇÃO (pulsante verde) */
.box-slot {
  border: 2px solid #10b981; background: #ecfdf5;
  color: #10b981; font-size: 0.9rem;
  animation: slotPulse 1.2s ease-in-out infinite;
}
@keyframes slotPulse {
  0%, 100% { box-shadow: 0 0 0 0 rgba(16,185,129,0.3); }
  50% { box-shadow: 0 0 0 8px rgba(16,185,129,0); }
}
.slot-label { color: #10b981; font-weight: 600; }

/* ADD NODE */
.box-add {
  border: 2px dashed #d1d5db; background: transparent;
  cursor: pointer; width: 44px; height: 44px;
  color: #d1d5db; font-size: 0.8rem; margin-top: 8px;
}
.box-add:hover, .flow-node-add-active .box-add {
  border-color: #6366f1; color: #6366f1; background: #f5f3ff;
}

/* WIRE SIMPLES */
.flow-wire {
  width: 24px; height: 1.5px; background: #d1d5db;
  margin-top: 30px; flex-shrink: 0; position: relative;
}
.flow-wire::before, .flow-wire::after {
  content: ''; position: absolute; top: -3px;
  width: 7px; height: 7px; border-radius: 50%;
  background: #fff; border: 1.5px solid #d1d5db;
}
.flow-wire::before { left: -4px; }
.flow-wire::after { right: -4px; }
/* WIRE ZONE (entre tokens) */
.flow-wire-zone {
  width: 28px; height: 60px;
  display: flex; align-items: center; justify-content: center;
  position: relative; flex-shrink: 0; cursor: pointer;
}
.flow-wire-line {
  width: 100%; height: 1.5px; background: #d1d5db;
  position: absolute; top: 50%;
}
.flow-wire-dot {
  position: absolute; top: 50%; transform: translateY(-50%);
  width: 7px; height: 7px; border-radius: 50%;
  background: #fff; border: 1.5px solid #d1d5db; z-index: 1;
}
.flow-wire-dot-l { left: -1px; }
.flow-wire-dot-r { right: -1px; }
.flow-wire-actions {
  position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%);
  display: flex; gap: 3px; opacity: 0; transition: opacity 0.15s; z-index: 2;
}
.flow-wire-zone:hover .flow-wire-actions { opacity: 1; }
.flow-wire-zone:hover .flow-wire-line { background: #fca5a5; height: 2px; }
.flow-wire-zone:hover .flow-wire-dot { border-color: #fca5a5; }
.flow-wire-btn {
  width: 20px; height: 20px; border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
  border: none; cursor: pointer; font-size: 0.5rem; transition: all 0.15s;
}
.flow-wire-btn-del {
  background: #ef4444; color: #fff;
  box-shadow: 0 2px 6px rgba(239,68,68,0.3);
}
.flow-wire-btn-del:hover { background: #dc2626; transform: scale(1.15); }

/* WIRE EXPAND (drag between nodes) — opens space for insertion */
.flow-wire-zone-expand {
  width: 70px; transition: width 0.2s ease;
}
.flow-wire-zone-expand .flow-wire-line { background: #10b981; height: 3px; border-radius: 2px; }
.flow-wire-zone-expand .flow-wire-dot { border-color: #10b981; background: #10b981; }

/* WIRE REJECTED (drop not allowed) */
.flow-wire-zone-rejected .flow-wire-line { background: #ef4444; height: 3px; border-radius: 2px; }
.flow-wire-zone-rejected .flow-wire-dot { border-color: #ef4444; background: #ef4444; }

/* ADD NODE REJECTED */
.flow-node-add-rejected .box-add {
  border-color: #ef4444; color: #ef4444; background: #fef2f2;
}
</style>
